<template>
  <div v-loading="isLoading" class="mini-chart">
    <div v-if="hasChartData" class="tiger-chart">
      <div class="chart-wrapper">
        <div :id="chartContainerId" ref="container" class="chart-container" />
      </div>
    </div>
    <NoData v-else :text="$t('quote.no-data')" />
  </div>
</template>
<script>
import Icon from 'com/Icon'
import Config from './config'
import { fetchCandleData } from './fetchData'
import stockMixin from './stockMixin'
import kChartMixin from './kChartMixin'
import choiceFilter from 'src/utils/choiceFilter'
import { mapGetters } from 'vuex'
import bus from 'src/eventbus'
import NoData from 'mod/NoData'

const PAGE_COUNT = 300
export default {
  name: 'MiniKChart',
  mixins: [stockMixin, kChartMixin],
  components: {
    NoData,
    Icon
  },
  props: {
    stock: {
      required: true,
      type: Object
    },
    type: Object,
    config: {
      type: Object,
      default: () => {}
    },
    chartContainerId: {
      type: String,
      default: 'chart_div'
    }
  },
  data: () => ({
    chartData: {},
    chartConfig: Object.assign({}, Config),
    canLoadMore: true,
    hasChartData: true,
    startItemTime: null,
    endItemTime: null,
    lastTime: null,
    csChart: null,
    newTypeName: '',
    needNewChart: false,
    newSymbol: '',
    isLoading: false
  }),
  computed: {
    ...mapGetters([
      'cnMarketOpen',
      'languageChartNeed',
      'isRedUp',
      'hkMarketAuction'
    ]),
    symbol() {
      if (!this.stock) return null
      const symbol = this.stock.webLocal
        ? this.stock.webLocal.symbol
        : this.stock.symbol
      if (!symbol) return null
      return symbol
    },
    typeName() {
      return this.type.name
    },
    market() {
      return this.stock.market
    },
    chartItems() {
      if (!this.hasChartData) return []
      if (Object.keys(this.chartData).length === 0) return []
      const items = this.chartData.items
      return items || []
    },
    latestTime() {
      return this.stock.timestamp || this.stock.serverTime
    },
    isCandle() {
      return this.type && !['minute', '5day'].includes(this.type.name)
    }
  },
  watch: {
    latestTime() {
      this.updateChart()
    },
    typeName() {
      this.hasChartData = true
      this.needNewChart = false
      this.chartData = {}
      this.miniCsSetConfig({
        key: 'candleScale',
        value: 1,
        isUpdate: false
      })
      this.newTypeName = this.typeName
      this.getKchartData()
    },
    symbol() {
      this.hasChartData = true
      this.needNewChart = true
      this.chartData = {}
      this.miniCsSetConfig({
        key: 'candleScale',
        value: 1,
        isUpdate: false
      })
      this.newSymbol = this.symbol
      this.getKchartData()
    },
    languageChartNeed(val) {
      window.LANGUAGE = val
      this.miniCsUpdataAll()
    },
    isRedUp() {
      this.miniCsSetConfig({
        key: 'upColor',
        value: this.isRedUp ? 'var(--red)' : 'var(--green)',
        isUpdate: false // 是否立刻更新 同时进行多个设置只需要更新一次即可
      })
      this.miniCsSetConfig({
        key: 'downColor',
        value: this.isRedUp ? 'var(--green)' : 'var(--red)',
        isUpdate: true
      })
    }
  },
  mounted() {
    this.lastTime = this.latestTime
    this.newTypeName = this.typeName
    this.newSymbol = this.symbol
    this.getKchartData()
    this.miniLoadKLineHistory = (symbol, count) => {
      if (!this.canLoadMore) return
      this.getChartData(count)
    }
    bus.on(bus.$events.MANUAL_REFRESH_QUOTE, this.getKchartData)
  },
  beforeDestroy() {
    this.csChart = null
    this.miniCsDestroyEvent()
    bus.off(bus.$events.MANUAL_REFRESH_QUOTE, this.getKchartData)
  },
  methods: {
    updateSize() {
      this.miniCsUpdataAll()
    },
    getKchartData() {
      if (!this.stock || !this.type || !this.typeName) return
      this.isLoading = true
      this.getChartData()
    },
    getChartData(index = 0) {
      if (this.newTypeName !== this.typeName) return
      if (this.newSymbol !== this.symbol) return
      if (index === 0) {
        this.startItemTime = null
        this.endItemTime = null
        this.canLoadMore = true
      }
      const params = this.getParams(index)
      fetchCandleData(params)
        .then(({ data }) => {
          this.isLoading = false
          if (!data) {
            if (index === 0 || Object.keys(this.chartData).length === 0) {
              this.hasChartData = false
            }
            return
          }
          if (!this.isFut && data.ret !== 0) {
            if (this.chartItems.length === 0) {
              this.hasChartData = false
            } else {
              this.canLoadMore = false
            }
            return
          }
          this.chartData = data
          if (!data.items || data.items.length === 0) {
            if (index === 0) {
              this.hasChartData = false
            } else {
              this.canLoadMore = false
            }
            return
          }
          const items = data.items
          if (index !== -1) {
            this.startItemTime = items[0].time
            this.endItemTime = null
          } else {
            // 期货返回数据是反的
            // 期货日K 周K 月K的time是收盘时间，分钟K的time是该根K线的开始时间，所以增量请求时，取lastTime和time中的较小值
            this.endItemTime = this.isFut
              ? Math.min(items[0].lastTime, items[0].time)
              : items[items.length - 1].time
          }
          this.hasChartData = true
          if (index === 0) {
            // 初次加载
            this.drawKchart()
          } else if (index === -1) {
            // 数据更新
            this.miniCsUpdateNewData(this.chartData, this.market)
          } else {
            // 加载历史数据
            this.miniCsLoadHistoryData(this.chartData, this.market)
          }
        })
        .catch(e => {
          this.$emit('loading.end')
          if (Object.keys(this.chartData).length === 0) {
            this.hasChartData = false
          }
        })
    },
    drawKchart() {
      if (!this.symbol) return
      if (Object.keys(this.chartData).length === 0) return

      if (this.csChart && !this.needNewChart) {
        this.miniCsUpdateAllData(this.chartData, this.market)
      } else {
        let config = {}
        const market = this.isAStock ? 'CN' : this.market
        const exchangeObj = {
          CN: 'astock',
          US: 'usstock',
          HK: 'hkex',
          UK: 'ukstock'
        }
        let symbolType = 'stk'
        if (this.isOpt) {
          symbolType = 'sop'
        } else if (this.isFut) {
          symbolType = 'fut'
        }
        let digitOffset = 2
        if (this.isFut) {
          digitOffset = this.stock.contract.productPriceConfig.maxScale
        } else {
          digitOffset = choiceFilter(this.stock)
        }
        config = Object.assign(
          this.chartConfig,
          {
            containerId: this.chartContainerId,
            symbol: this.symbol,
            country: this.market.toLowerCase(),
            exchange: this.isFut
              ? this.stock.exchange.code
              : exchangeObj[market],
            symbolType,
            digitOffset,
            displayType: 'normal',
            upColor: this.isRedUp ? 'var(--red)' : 'var(--green)',
            downColor: this.isRedUp ? 'var(--green)' : 'var(--red)',
            lang: this.languageChartNeed
          },
          this.config
        )
        if (this.isFut) {
          const contract = this.stock.contract
          config = Object.assign(config, {
            multiplier: contract.productPriceConfig.displayMultiplier.value,
            displayType: contract.quotesDisplayType.toLowerCase()
          })
        }
        if (!this.isCandle) return
        this.csChart = this.newMiniCandleChart(config, this.chartData)
        this.needNewChart = false
      }
    },
    updateChart() {
      if (this.newSymbol !== this.symbol) return
      if (this.stock.symbol !== this.chartData.symbol) return
      if (!this.stock || this.isLoading) return
      if (this.isFut && !this.stock.price) return
      let intervalTime = 2 * 1000
      // 竞价时间段，1秒刷新
      if (this.hkMarketAuction) {
        intervalTime = 1000
      }
      if (this.latestTime - this.lastTime > intervalTime) {
        this.getChartData(-1)
        this.lastTime = this.latestTime
      } else {
        this.updateLastPrice()
      }
    },
    updateLastPrice() {
      // A股竞价时不更新价格
      if (this.isAStock && !this.cnMarketOpen) return
      // 英股港股竞价时不更新最新价格
      if (this.hkMarketAuction) return
      let latestPrice = 0
      // 长连接更新
      if (this.isFut && this.stock.price.mi) {
        latestPrice = this.stock.price.mi.p
      } else if (!this.isFut && this.stock.latestPrice) {
        latestPrice = this.stock.latestPrice
      } else {
        return
      }
      if (!latestPrice) {
        return
      }
      this.miniCsUpdateLastPrice(latestPrice)
    },
    getParams(index) {
      let params = {
        market: this.market,
        period: this.typeName,
        secType: this.stock.secType
      }
      if (this.isFut) {
        params.contractCode = this.symbol
        if (index === -1 && this.endItemTime) {
          params.time = this.endItemTime
        } else {
          params.index = index
          params.offset = PAGE_COUNT
        }
      } else {
        params = Object.assign(params, {
          symbol: this.stock.symbol,
          startTime: index === -1 && this.endItemTime ? this.endItemTime : -1,
          endTime: index !== -1 && this.startItemTime ? this.startItemTime : -1, // 查看历史数据时
          limit: PAGE_COUNT // 设置较大的limit，减弱用户感受到的翻页卡顿
        })
        if (this.isOpt) {
          params = Object.assign(params, {
            strike: this.stock.strike,
            right: this.stock.right,
            expiry: this.stock.expiry
          })
        }
      }
      return params
    }
  }
}
</script>
<style lang="less" src="./styles/chart.less" scoped />
<style lang="less" src="./styles/widgets.less" />
